import React, { useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import SEO from '../../components/SEO';
import PublicHeader from '../../components/PublicHeader';
import PublicFooter from '../../components/PublicFooter';
import './PublicPages.css';

export default function GroupRestaurantDecisionPage() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="public-page">
      <SEO
        title="Group Restaurant Decision — Pick Where to Eat with Friends | Hangrily"
        description="Deciding where to eat with a group? Hangrily's Group Mode lets up to 10 people swipe on nearby restaurants and finds the consensus pick everyone agrees on. Share a code, swipe at your desk, done in 2 minutes. Free."
        path="/group-restaurant-decision"
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: 'Group Restaurant Decision - Pick Where to Eat with Friends',
            description: 'The democratic way to decide where to eat as a group. Everyone swipes, everyone agrees.',
            url: 'https://hangrily.app/group-restaurant-decision'
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://hangrily.app' },
              { '@type': 'ListItem', position: 2, name: 'Group Restaurant Decision', item: 'https://hangrily.app/group-restaurant-decision' }
            ]
          },
          {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: [
              {
                '@type': 'Question',
                name: 'How many people can join a group session?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Up to 10 participants can join a single group session. Each person swipes independently on their own device, and the algorithm factors in everyone\'s preferences equally.'
                }
              },
              {
                '@type': 'Question',
                name: 'Does everyone need to download the app?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Everyone needs a free Hangrily account. You can use it on any device through a web browser or download the mobile app. Registration takes 30 seconds.'
                }
              },
              {
                '@type': 'Question',
                name: 'What if not everyone has the same food preferences?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'That\'s exactly what Hangrily is designed for. The algorithm finds the best overlap between different preferences. Even diverse groups usually have some common ground — and Hangrily finds it.'
                }
              },
              {
                '@type': 'Question',
                name: 'How long does a group session take?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Most group sessions complete in 3-5 minutes, depending on group size and how quickly everyone swipes. It\'s way faster than any group chat debate.'
                }
              }
            ]
          }
        ]}
      />
      <PublicHeader />

      <main className="page-content">
        <section className="page-hero">
          <h1>Group Restaurant Decision Made Easy</h1>
          <p className="hero-subtitle">
            10 people. 10 opinions. 1 perfect restaurant. Decided in under 5 minutes.
          </p>
        </section>

        <section className="content-section">
          <div className="two-column-layout">
            <div className="column-text">
              <h2>The Group Chat Is Not a Restaurant Picker</h2>
              <p>
                We've all been there. Someone texts "where should we eat tonight?" and the group chat 
                explodes with 15 different suggestions, 7 vetoes, and 3 people who "don't care." 
                An hour later, you still haven't decided and everyone's hangry.
              </p>
              <p>
                Hangrily's <strong>Group Mode</strong> replaces that chaos with a simple, democratic system. 
                One person creates a session and shares a code. Everyone joins, sets their preferences, 
                and swipes on restaurants. The app handles the rest — finding the one restaurant that 
                the most people agreed on.
              </p>
              <button className="btn btn-primary btn-lg" onClick={() => navigate('/try')}>
                Start a Group Session →
              </button>
            </div>
            <div className="column-visual">
              <div className="feature-showcase-box">
                <h3>👥 How Group Mode Works</h3>
                <ol className="showcase-steps">
                  <li><strong>Create:</strong> Start a session and get a unique 6-digit code</li>
                  <li><strong>Share:</strong> Send the code to your group via text, email, or chat</li>
                  <li><strong>Join:</strong> Everyone opens Hangrily and enters the code</li>
                  <li><strong>Swipe:</strong> All participants swipe on nearby restaurants</li>
                  <li><strong>Match:</strong> The restaurant with the most agreement wins</li>
                </ol>
              </div>
            </div>
          </div>
        </section>

        <section className="content-section alt-bg">
          <h2 className="section-title">Why Groups Love Hangrily</h2>
          <div className="reason-cards">
            <div className="reason-card">
              <div className="reason-icon">🗳️</div>
              <h3>Democratic Voting</h3>
              <p>
                Every person's swipe counts equally. No one person dominates the decision. The loudest 
                voice doesn't win — the most popular restaurant does. It's fair, fast, and final.
              </p>
            </div>
            <div className="reason-card">
              <div className="reason-icon">🔗</div>
              <h3>Easy Code Sharing</h3>
              <p>
                No need to add friends or create accounts for everyone. Just share a 6-digit code via 
                any messaging app. Anyone with the code can join the session in seconds.
              </p>
            </div>
            <div className="reason-card">
              <div className="reason-icon">⚡</div>
              <h3>Real-Time Progress</h3>
              <p>
                See who's joined, who's swiping, and who's done — all in real time. No more "did everyone 
                vote?" messages. The app tells you when the group is ready for results.
              </p>
            </div>
            <div className="reason-card">
              <div className="reason-icon">🎯</div>
              <h3>Smart Consensus</h3>
              <p>
                Our algorithm doesn't just count votes. It weighs likes and loves differently, handles 
                ties intelligently, and uses multiple rounds to narrow down options. The result: a 
                restaurant everyone can genuinely get behind.
              </p>
            </div>
            <div className="reason-card">
              <div className="reason-icon">👥</div>
              <h3>Up to 10 People</h3>
              <p>
                Whether it's 3 friends grabbing lunch or 10 coworkers planning a team dinner, Group Mode 
                scales to handle your entire crew.
              </p>
            </div>
            <div className="reason-card">
              <div className="reason-icon">🥈</div>
              <h3>Runner-Up Options</h3>
              <p>
                If the top pick is full or closed, you've got backup options ready. No need to restart — 
                just move to the next restaurant on the list.
              </p>
            </div>
          </div>
        </section>

        <section className="content-section">
          <h2 className="section-title">Perfect for Every Group Situation</h2>
          <div className="use-case-grid">
            <div className="use-case-card">
              <span className="use-case-icon">🍻</span>
              <h3>Friends Night Out</h3>
              <p>Friday night, nobody can agree. Share a code in the group chat, swipe for 2 minutes, and you're done.</p>
            </div>
            <div className="use-case-card">
              <span className="use-case-icon">👨‍👩‍👧‍👦</span>
              <h3>Family Dinners</h3>
              <p>Mom wants Italian, Dad wants steaks, kids want burgers. Hangrily finds the overlap everyone's happy with.</p>
            </div>
            <div className="use-case-card">
              <span className="use-case-icon">💼</span>
              <h3>Team Lunches</h3>
              <p>Office lunch planning made easy. Share a code on Slack, everyone swipes at their desk, decision made before noon.</p>
            </div>
            <div className="use-case-card">
              <span className="use-case-icon">🎉</span>
              <h3>Birthday Dinners</h3>
              <p>Planning a birthday dinner? Let the whole group help pick the perfect spot — birthday person's loves get priority.</p>
            </div>
          </div>
        </section>

        <section className="content-section alt-bg">
          <h2 className="section-title">Better Than Other Group Decision Methods</h2>
          <div className="comparison-grid">
            <div className="comparison-item lose">
              <h3>❌ Group Chat Suggestions</h3>
              <p>50 messages, 12 suggestions, 8 vetoes, 2 people who "don't care." An hour later, still no decision.</p>
            </div>
            <div className="comparison-item lose">
              <h3>❌ One Person Decides</h3>
              <p>Someone always picks the same place, others feel ignored, and half the group is disappointed before dinner starts.</p>
            </div>
            <div className="comparison-item lose">
              <h3>❌ Random Wheel Spin</h3>
              <p>No preferences considered. Could land on someone's least favorite place. Not fair and not fun.</p>
            </div>
            <div className="comparison-item win">
              <h3>✅ Hangrily Group Mode</h3>
              <p>Everyone gets equal say. Preferences are respected. Democratic algorithm finds consensus. 5 minutes max.</p>
            </div>
          </div>
        </section>

        <section className="content-section">
          <h2 className="section-title">Frequently Asked Questions</h2>
          <div className="faq-grid">
            <div className="faq-item">
              <h3>How many people can join a group session?</h3>
              <p>
                Up to 10 participants can join a single group session. Each person swipes independently 
                on their own device, and the algorithm factors in everyone's preferences equally.
              </p>
            </div>
            <div className="faq-item">
              <h3>Does everyone need to download the app?</h3>
              <p>
                Everyone needs a free Hangrily account. You can use it on any device through a web browser 
                or download the mobile app. Registration takes 30 seconds.
              </p>
            </div>
            <div className="faq-item">
              <h3>What if not everyone has the same food preferences?</h3>
              <p>
                That's exactly what Hangrily is designed for. The algorithm finds the best overlap between 
                different preferences. Even diverse groups usually have some common ground — and Hangrily finds it.
              </p>
            </div>
            <div className="faq-item">
              <h3>How long does a group session take?</h3>
              <p>
                Most group sessions complete in 3-5 minutes, depending on group size and how quickly 
                everyone swipes. It's way faster than any group chat debate.
              </p>
            </div>
          </div>
          <div style={{ textAlign: 'center', marginTop: '32px' }}>
            <Link to="/faq" className="btn btn-secondary btn-lg">See All FAQs →</Link>
          </div>
        </section>

        <section className="cta-banner">
          <h2>Your Group's Next Dinner, Decided in 5 Minutes</h2>
          <p>No more group chat debates. No more someone always picking. Just a restaurant everyone agrees on.</p>
          <button className="btn btn-primary btn-lg" onClick={() => navigate('/try')}>
            Create a Group Session →
          </button>
        </section>
      </main>

      <PublicFooter />
    </div>
  );
}
